import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import logger from '../utils/logger.js';
import { getDefaultFilters } from "../components/NotificationPanel/notificationConstants.js";

const NotificationContext = createContext(null);

const STORAGE_KEY = "scx_notifications";
const MAX_NOTIFICATIONS = 100;

/**
 * Map severity/variant to GlobalNotificationCenter status
 */
const mapSeverityToStatus = (severity) => {
    const mapping = {
        success: "success",
        error: "error",
        warning: "warning",
        info: "informational",
        information: "informational",
        informational: "informational"
    };
    return mapping[severity?.toLowerCase()] || "informational";
};

/**
 * Generate unique notification ID using cryptographically secure random values
 */
const generateNotificationId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `notif-${crypto.randomUUID()}`;
    }
    // Fallback for environments without crypto.randomUUID
    return `notif-${Date.now()}-${Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, "0")).join("")}`;
};

/**
 * Load notifications from sessionStorage
 */
const loadStoredNotifications = () => {
    if (typeof sessionStorage === "undefined") {
        return [];
    }
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return [];
        }
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        logger.warn("[NotificationContext] Failed to load stored notifications", error);
        return [];
    }
};

/**
 * Save notifications to sessionStorage
 */
const saveNotifications = (notifications) => {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    try {
        // Keep only the most recent notifications
        const toStore = notifications.slice(0, MAX_NOTIFICATIONS);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
        logger.warn("[NotificationContext] Failed to save notifications", error);
    }
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState(() => loadStoredNotifications());
    const [filters, setFilters] = useState(getDefaultFilters);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    // Persist notifications to localStorage when they change
    useEffect(() => {
        saveNotifications(notifications);
    }, [notifications]);

    /**
     * Add a new notification
     * @param {Object} payload - Notification payload
     * @param {string} payload.title - Notification title
     * @param {string} payload.message - Notification body message
     * @param {string} payload.variant - Severity: success, error, warning, info
     * @param {string} [payload.actionLabel] - Optional action link text
     * @param {Function} [payload.onAction] - Optional action callback
     * @param {string} [payload.productName] - Optional product name
     */
    const addNotification = useCallback((payload) => {
        const newNotification = {
            id: generateNotificationId(),
            timestamp: Date.now(),
            title: payload.title || "Notification",
            body: payload.message || "",
            status: mapSeverityToStatus(payload.variant || payload.severity),
            actionLabel: payload.actionLabel || undefined,
            navigationTarget: payload.navigationTarget || null,
            read: false,
            flagged: false,
            pinned: false,
            productName: payload.productName || "SCX",
            // Store the action callback reference (won't persist to localStorage)
            _onAction: payload.onAction || null
        };

        setNotifications((prev) => [newNotification, ...prev].slice(0, MAX_NOTIFICATIONS));

        return newNotification.id;
    }, []);

    /**
     * Update a notification (mark read, flag, pin, etc.)
     */
    const updateNotification = useCallback((updatedNotification) => {
        setNotifications((prev) =>
            prev.map((n) =>
                n.id === updatedNotification.id
                    ? { ...n, ...updatedNotification }
                    : n
            )
        );
    }, []);

    /**
     * Archive (remove) a notification
     */
    const archiveNotification = useCallback((notification) => {
        setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    }, []);

    /**
     * Undo archive - restore a notification
     */
    const undoArchiveNotification = useCallback((notification) => {
        setNotifications((prev) => {
            // Check if already exists
            if (prev.some((n) => n.id === notification.id)) {
                return prev;
            }
            // Re-add in chronological order
            const updated = [notification, ...prev].sort((a, b) => b.timestamp - a.timestamp);
            return updated.slice(0, MAX_NOTIFICATIONS);
        });
    }, []);

    /**
     * Mark all notifications as read
     */
    const markAllAsRead = useCallback(() => {
        setNotifications((prev) =>
            prev.map((n) => ({ ...n, read: true }))
        );
    }, []);

    /**
     * Get unread count
     */
    const unreadCount = useMemo(() => {
        return notifications.filter((n) => !n.read).length;
    }, [notifications]);

    /**
     * Filter notifications based on current filters
     */
    const filteredNotifications = useMemo(() => {
        let result = [...notifications];

        // Filter by unread
        if (filters.unread) {
            result = result.filter((n) => !n.read);
        }

        // Filter by flagged
        if (filters.flagged) {
            result = result.filter((n) => n.flagged);
        }

        // Filter by status
        if (filters.selectedStatuses?.length > 0) {
            result = result.filter((n) => filters.selectedStatuses.includes(n.status));
        }

        // Filter by product
        if (filters.selectedProducts?.length > 0) {
            result = result.filter((n) => filters.selectedProducts.includes(n.productName));
        }

        // Sort
        if (filters.sortOrder === "oldest") {
            result.sort((a, b) => a.timestamp - b.timestamp);
        } else {
            result.sort((a, b) => b.timestamp - a.timestamp);
        }

        // Pinned items always at top
        const pinned = result.filter((n) => n.pinned);
        const unpinned = result.filter((n) => !n.pinned);

        return [...pinned, ...unpinned];
    }, [notifications, filters]);

    /**
     * Clear all notifications
     */
    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    /**
     * Open the notification panel
     */
    const openPanel = useCallback(() => {
        setIsPanelOpen(true);
    }, []);

    /**
     * Close the notification panel
     */
    const closePanel = useCallback(() => {
        setIsPanelOpen(false);
    }, []);

    /**
     * Refresh notifications (placeholder for API integration)
     */
    const refresh = useCallback(() => {
        // Future: fetch notifications from API
        // For now, just reload from storage
        const stored = loadStoredNotifications();
        setNotifications(stored);
    }, []);

    const contextValue = useMemo(() => ({
        // State
        notifications,
        filteredNotifications,
        filters,
        isPanelOpen,
        unreadCount,
        // Actions
        addNotification,
        updateNotification,
        archiveNotification,
        undoArchiveNotification,
        markAllAsRead,
        clearAllNotifications,
        setFilters,
        openPanel,
        closePanel,
        refresh
    }), [
        notifications,
        filteredNotifications,
        filters,
        isPanelOpen,
        unreadCount,
        addNotification,
        updateNotification,
        archiveNotification,
        undoArchiveNotification,
        markAllAsRead,
        clearAllNotifications,
        openPanel,
        closePanel,
        refresh
    ]);

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
        </NotificationContext.Provider>
    );
};

NotificationProvider.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * Hook to access notification context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotifications must be used within NotificationProvider");
    }
    return context;
};

export default NotificationContext;
